import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as S from './Logs.styled';
import { invalidateLatestActivityLogsCache, useLogs, useLogsSelector, useWindowSize } from './hooks';
import Loader from 'pages/components/Loader/Loader';
import { LoaderCard } from 'pages/components/Loader/Loader';
import LogTable from './components/LogTable';
import ActivitySearch from './components/ActivitySearch';
import ActivityLogsSearchControls from './components/ActivityLogsSearchControls';
import * as SearchS from './components/ActivitySearch.styled';
import TmpPanel from 'pages/components/TmpPanel/TmpPanel';
import QuickOptions from './components/QuickOptions';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  logKeys0, logTypes0, selectActivityLogsCombinedChecked, setAssociateElevatedActivityLogs, setComplianceActivity,
  setLogSearch, setLogSearchOptions, setLogSearches, setLogsChecked, setLogsValueMode,
  setRecentLog, setSingleLogTime, setActivityLogsViewAs, setActivityLogsOnlyLogIds, setActivityLogsStartEndLogIds,
  setActivityLogsCombineSku, setActivityLogsTypeAttributeMode, selectLogs,
  DEFAULT_LOG_SEARCH_OPTIONS,
  selectIsActivityLogsReportLogsFocus,
  ACTIVITY_LOGS_REPORT_LOGS_FOCUS_GROUPS_TOAST,
  selectActivityLogsSearchStatus,
  selectActivityLogsSearchQueued,
  setActivityLogsSearchStatus,
  setActivityLogsSearchQueued,
} from './redux';
import { selectIsSocketConnected, selectOrganizationData } from 'pages/redux';
import { canUseActivityTools } from 'pages/Settings/redux';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { ActivitySelector } from './components/ActivitySelector';
import { ModalParts } from 'pages/redux';
import Modal from 'pages/components/Modal/Modal';
import RightBar from 'pages/components/RightBar/RightBar';
import { consumeLogsJump, parseLogsJumpFromSearch, peekLogsJump } from './logUtils';
import { useLocation } from 'react-router-dom';
import { toast } from 'utils/toast';
import { logButtonClick } from 'utils/analytics';
import Search from '@mui/icons-material/Search';
import { ACTIVITY_LOGS_LABEL_MEDIUM, ACTIVITY_LOGS_LABEL_SHORT } from './activityLogLabels';
import { clearActivityLogsPageMemoryCache } from 'utils/activityLogsMergedPool';

export const logTypes = logTypes0;
export type LogType = typeof logTypes[number];

export const logKeys = logKeys0;
export type LogKey = typeof logKeys[number];

export type QuickLinks = {
  name: string,
  func: Function
}

export type LogGroups = {
  Departments: string[],
  'Department entries': ('Direct' | 'Expense' | 'Document')[],
  Inventory: string[],
  'Inventory Group': string[],
  // Staff, settings, activityLogs, and inventory lifecycle — no hospitality or CRM types
  Actions: (
    'Staff logged in' |
    'Compliance favorable' | 'Compliance note' | 'Compliance incident' | 'Compliance alert' |
    'Staff added' | 'Staff change' | 'Staff removed' | 'Settings change' |
    'Log change' | 'Log deleted' | 'Log restored' |
    'Inventory Added' | 'Inventory Renamed' | 'Inventory deleted' | 'Report sent' | 'Report filed' | 'Report closed' |
    'Department added' | 'Department renamed' | 'Department removed' | 'Department change' |
    'Workflow added' | 'Workflow changed' | 'Workflow published' | 'Workflow archived' | 'Workflow removed' |
    'Workflow task done' | 'Workflow approved' | 'Workflow rejected'
  )[],
}

/** Thin shim for Metrics / Reporting — left pane uses dynamic activityGroupsModel. */
export const logGroupsRaw: LogGroups = {
  Departments: [],
  'Department entries': ['Direct', 'Expense', 'Document'],
  Inventory: [],
  'Inventory Group': [],
  Actions: [
    'Staff logged in',
    'Compliance favorable', 'Compliance note', 'Compliance incident', 'Compliance alert',
    'Staff added', 'Staff change', 'Staff removed', 'Settings change',
    'Log change', 'Log deleted', 'Log restored',
    'Inventory Added', 'Inventory Renamed', 'Inventory deleted',
    'Report sent',
    'Report filed',
    'Report closed',
    'Department added', 'Department renamed', 'Department removed', 'Department change',
    'Workflow added', 'Workflow changed', 'Workflow published', 'Workflow archived', 'Workflow removed',
    'Workflow task done', 'Workflow approved', 'Workflow rejected',
  ],
};

const Logs = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { runActivityLogsSearch } = useLogs();
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState<ModalParts | null>(null);
  const [rightBar, setRightBar] = useState<ModalParts | null>(null);

  const triggerAllItems0 = () => {
    setTriggerAllItems(!triggerAllItems);
  };

  const triggerAllItems1 = (key: LogKey, _logType: string) => {
    if (key !== 'Departments' && key !== 'Department entries'
      && key !== 'Inventory' && key !== 'Inventory Group') {
      setTriggerAllItems(!triggerAllItems);
      return;
    }
    if (key === 'Departments' || key === 'Department entries'
      || key === 'Inventory' || key === 'Inventory Group') {
      setTriggerCashIn(!triggerCashIn);
      setTriggerCashOut(!triggerCashOut);
    }
  };

  const {
    onClickShowAll
  } = useLogsSelector({
    triggerAllItems0,
    triggerAllItems1,
  });
  const checked = useAppSelector(selectActivityLogsCombinedChecked);
  const permission = useAppSelector(selectPermission);
  const staffId = useAppSelector(selectStaffId);
  const username = useAppSelector(selectUsername);
  const liveLogs = useAppSelector(selectLogs);
  const organizationData = useAppSelector(selectOrganizationData);
  const isSocketConnected = useAppSelector(selectIsSocketConnected);
  const isReportLogsFocus = useAppSelector(selectIsActivityLogsReportLogsFocus);
  const activityLogsSearchStatus = useAppSelector(selectActivityLogsSearchStatus);
  const activityLogsSearchQueued = useAppSelector(selectActivityLogsSearchQueued);
  const initialLatestFetchStarted = useRef(false);
  const [triggerAllItems, setTriggerAllItems] = useState<boolean>(false);
  const [triggerCashIn, setTriggerCashIn] = useState<boolean>(false);
  const [triggerCashOut, setTriggerCashOut] = useState<boolean>(false);

  const origQuickLinks: QuickLinks[] = [
    {
      name: 'Show all',
      func: (check: QuickLinks['name'][]) => {
        if (isReportLogsFocus) {
          toast(ACTIVITY_LOGS_REPORT_LOGS_FOCUS_GROUPS_TOAST, { type: 'info' });
          return;
        }
        onClickShowAll(check);
        triggerAllItems0();
      }
    },
  ];

  const startQLinks = [...origQuickLinks];
  startQLinks.splice(1, 3);
  const [quickLinks, setQuickLinks] = useState<QuickLinks[]>(startQLinks);
  const { width: windowW } = useWindowSize();

  useEffect(() => {
    const qLinks = [...origQuickLinks];
    if (windowW) {
      if (windowW <= 403) {
        qLinks.splice(1, 3);
        setQuickLinks(qLinks);
        return;
      }
      if (windowW < 577) {
        qLinks.splice(2, 2);
        setQuickLinks(qLinks);
        return;
      }
      if (windowW < 768) {
        qLinks.splice(3, 1);
        setQuickLinks(qLinks);
      } else {
        setQuickLinks(qLinks);
      }
    }
  }, [windowW]);

  useEffect(() => {
    const logsPath = location.pathname;
    return () => {
      // Strict Mode remounts on the same path — clearing here wiped cash-session
      // onlyLogIds before the jump re-applied and raced the queued search (₦0 → ₦39).
      try {
        if (window.location.pathname === logsPath) return;
      } catch {
        /* ignore */
      }
      // Clear session-only ActivityLogs modes when leaving — keep time range and search
      // results (activityLogsSearchStatus + logs) so return visits still show the table.
      dispatch(setAssociateElevatedActivityLogs(false));
      dispatch(setComplianceActivity({ active: false }));
      dispatch(setActivityLogsViewAs(null));
      dispatch(setActivityLogsOnlyLogIds(null));
      dispatch(setActivityLogsStartEndLogIds(null));
      dispatch(setActivityLogsSearchQueued(false));
    };
  }, [dispatch, location.pathname]);

  // Sessions → /logs always sends elevate=1. Hold the table until jump filters apply.
  const pendingSessionsJump = useMemo(() => {
    const jump = parseLogsJumpFromSearch(location.search);
    return Boolean(jump?.elevate);
  }, [location.search]);
  const [sessionsJumpReady, setSessionsJumpReady] = useState(!pendingSessionsJump);

  // Page load always hits the network; session memory cache is only for paginator back/forward.
  useEffect(() => {
    const orgId = organizationData?.id;
    if (orgId == null || orgId === '') return;
    if (!isSocketConnected) return;
    if (pendingSessionsJump || activityLogsSearchQueued) return;
    if (initialLatestFetchStarted.current) return;

    // One auto-fetch per mount — do not reset on failure or /logs flashes forever when BE is down.
    initialLatestFetchStarted.current = true;
    clearActivityLogsPageMemoryCache();
    invalidateLatestActivityLogsCache();
    void runActivityLogsSearch('latest', 1, { force: true });
  }, [
    activityLogsSearchQueued,
    dispatch,
    isSocketConnected,
    organizationData?.id,
    pendingSessionsJump,
    runActivityLogsSearch,
  ]);

  useLayoutEffect(() => {
    const fromUrl = parseLogsJumpFromSearch(location.search);
    const stashed = peekLogsJump();
    const jump = fromUrl || consumeLogsJump();
    if (!jump) {
      setSessionsJumpReady(true);
      return undefined;
    }
    // URL may omit `only` — keep the exact chip ids from the report / cash-sessions click.
    if (jump.onlyLogIds == null && stashed?.onlyLogIds?.length) {
      jump.onlyLogIds = stashed.onlyLogIds;
    }
    // Stash has the real compliance flag; URL elevate≠compliance for cash-session opening.
    if (stashed && fromUrl) {
      if (typeof stashed.isComplianceActivity === 'boolean') {
        jump.isComplianceActivity = stashed.isComplianceActivity;
      }
      if (stashed.cashierId && !jump.cashierId) {
        jump.cashierId = stashed.cashierId;
      }
    }
    dispatch(setLogsChecked(jump.checked));
    dispatch(setLogSearches([]));
    dispatch(setLogSearch(jump.search || ''));
    dispatch(setLogSearchOptions({
      ...DEFAULT_LOG_SEARCH_OPTIONS,
      negate: Boolean(jump.searchNegate),
      matchMode: jump.searchMatchMode || 'any',
      exactMatch: Boolean(jump.searchExactMatch),
      hideArchives: Boolean(jump.hideArchives),
    }));
    dispatch(setAssociateElevatedActivityLogs(Boolean(jump.elevate)));
    // elevate ≠ compliance (cash-session opening uses elevate without cashier compliance).
    dispatch(setComplianceActivity({
      active: Boolean(jump.isComplianceActivity),
      staffId: jump.cashierId || null,
      username: jump.cashierUsername || null,
    }));
    dispatch(setLogsValueMode(jump.valueMode === 'units' ? 'units' : 'value'));
    dispatch(setActivityLogsStartEndLogIds(
      jump.startLogId || jump.endLogId
        ? { startLogId: jump.startLogId, endLogId: jump.endLogId }
        : null,
    ));
    // Time range is filled from the start/end log ids when those logs are loaded.
    const startLog = jump.startLogId
      ? (liveLogs || []).find((log) => String(log.id) === String(jump.startLogId))
      : null;
    const endLog = jump.endLogId
      ? (liveLogs || []).find((log) => String(log.id) === String(jump.endLogId))
      : null;
    const startMs = startLog
      ? +new Date(startLog.date)
      : (jump.startDate ? +new Date(jump.startDate) : NaN);
    const endMs = endLog
      ? +new Date(endLog.date)
      : (jump.endDate ? +new Date(jump.endDate) : NaN);
    if (Number.isFinite(startMs) || Number.isFinite(endMs)) {
      const fromMs = Number.isFinite(startMs) ? startMs : endMs;
      const toMs = Number.isFinite(endMs) ? endMs : startMs;
      dispatch(setSingleLogTime({
        startDate: new Date(Math.min(fromMs, toMs)),
        endDate: new Date(Math.max(fromMs, toMs)),
      }));
    }
    // Staff report Show logs: temporarily use the other party's ActivityLogs visibility.
    const viewAsIsMe = Boolean(
      jump.viewAsPermission != null
      && (
        (staffId && jump.viewAsStaffId && String(jump.viewAsStaffId) === String(staffId))
        || (
          username
          && jump.viewAsUsername
          && String(jump.viewAsUsername).trim().toLowerCase()
            === String(username).trim().toLowerCase()
        )
      ),
    );
    if (
      !viewAsIsMe
      && jump.viewAsPermission != null
      && Number.isFinite(Number(jump.viewAsPermission))
    ) {
      dispatch(setActivityLogsViewAs({
        permission: Number(jump.viewAsPermission),
        staffId: jump.viewAsStaffId || null,
        username: jump.viewAsUsername || null,
        label: jump.viewAsLabel || null,
      }));
    } else {
      dispatch(setActivityLogsViewAs(null));
    }
    // Exact chip / Total / cash-session ids (non-empty; empty figure uses sentinel id).
    dispatch(setActivityLogsOnlyLogIds(
      jump.onlyLogIds?.length ? jump.onlyLogIds : null,
    ));
    dispatch(setActivityLogsCombineSku(Boolean(jump.combineSku)));
    // Restore the Activity-header attribute the clicked report logs were shown with.
    if (jump.typeAttributeMode) {
      dispatch(setActivityLogsTypeAttributeMode(jump.typeAttributeMode));
    }
    // Optional: land on a specific activity after filters apply.
    if (jump.focusLogId) {
      dispatch(setRecentLog(String(jump.focusLogId)));
    }
    dispatch(setActivityLogsSearchStatus('loading'));
    dispatch(setActivityLogsSearchQueued(true));
    // Sessions only: keep loader up for a paint so the unfiltered table never flashes.
    if (!jump.elevate) {
      setSessionsJumpReady(true);
      return undefined;
    }
    setSessionsJumpReady(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSessionsJumpReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [dispatch, location.search, staffId, username]);

  useEffect(() => {
    if (!activityLogsSearchQueued) return;
    dispatch(setActivityLogsSearchQueued(false));
    void runActivityLogsSearch('search');
    // runActivityLogsSearch is recreated each render — only run when a search is queued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityLogsSearchQueued, dispatch]);

  const showOverlayLoader = isLoading;

  return (
    <S.Container>
      {showOverlayLoader && (
        <Loader message="Saving…" />
      )}
      {
        !!modal && (
          <Modal
            component={modal.component}
            exitOnBgClick={modal.exitOnBgClick}
          />
        )
      }
      <RightBar modal={rightBar} setModal={setRightBar} />
      {
        canUseActivityTools(permission) && sessionsJumpReady ? (
          <>
            <TmpPanel component={<ActivitySelector setModal={setModal} />} id="tmp_panel0" />
            <ActivitySelector isWeb setModal={setModal} />
            <S.ActivityLogsPanelMobile>
              <S.ActivityLogsText>{ACTIVITY_LOGS_LABEL_SHORT}</S.ActivityLogsText>
              <QuickOptions
                quickLinks={quickLinks}
                checked={checked}
                buttonLabel={`Select ${ACTIVITY_LOGS_LABEL_MEDIUM}`}
                lightPrimaryButton
              />
            </S.ActivityLogsPanelMobile>
          </>
        ) : (null)
      }
      {
        activityLogsSearchStatus === 'loading' ? (
          <S.RightPane>
            <SearchS.LoaderSlot>
              <LoaderCard inPage message="Loading logs…" />
            </SearchS.LoaderSlot>
          </S.RightPane>
        ) : activityLogsSearchStatus === 'results' ? (
          <S.RightPane>
            <SearchS.ResultsBar>
              <ActivityLogsSearchControls setModal={setModal} layout="bar" />
              <SearchS.NewSearchBtn
                type="button"
                data-analytics="activityLogs_new_search"
                onClick={() => {
                  logButtonClick('activityLogs_new_search');
                  dispatch(setActivityLogsSearchStatus('idle'));
                }}
              >
                <Search sx={{ fontSize: 18 }} />
                New search
              </SearchS.NewSearchBtn>
            </SearchS.ResultsBar>
            <LogTable
              checked={checked}
              triggerAllItems={triggerAllItems}
              triggerCashIn={triggerCashIn}
              triggerCashOut={triggerCashOut}
              setModal={setModal}
              setRightBar={setRightBar}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          </S.RightPane>
        ) : (
          <S.RightPane>
            <ActivitySearch
              setModal={setModal}
              onSearch={() => { void runActivityLogsSearch('search'); }}
              onLatest={() => { void runActivityLogsSearch('latest', 1, { force: true }); }}
            />
          </S.RightPane>
        )
      }
    </S.Container>
  );
};

export default Logs;
